/** Save of the Pre-check-in tab's draft: the Ledger's changeset compiled into the operator's
 *  PUT (`compileOperatorPreCheck`) with the ledger's description as the required reason.
 *
 *  - **409 `pre_check_modified`**: refetch the GET (`staleTime: 0` — the new baselines reach
 *    the DraftProvider, which re-checks every entry), then compare this save's changes with
 *    it (`rebaseChanges`). Nothing it touched moved (another setting, a tour relabelling a
 *    shared field) → recompile on the fresh GET and resend ONCE with its version. Otherwise
 *    reject with `{drifted}`: the Ledger rebases those entries and says so — Keep mine /
 *    Take theirs per change. A second 409 refetches and reports the same way.
 *  - **400**: the paths onto change keys (`operatorPreCheckFailure`).
 *  - **Success**: `onSaved` first (the Ledger resets the draft before the response
 *    re-seeds the baselines), then the response — the GET shape — goes into the query cache.
 *    The receipt is synthetic (the PUT returns no audit row to link). */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { MutableRefObject } from 'react';

import {
  getOperatorPreCheck,
  isPreCheckModified,
  operatorPreCheckKey,
  preCheckErrors,
  putOperatorPreCheck,
  type OperatorPreCheck,
} from '@/api/operatorPrecheck';
import {
  changeKey,
  draftValuesEqual,
  type DraftWireChange,
  type RecordDriftedField,
  type RecordKey,
} from '@/features/records/draft/changeSet';
import type { RecordApplyMutation, RecordApplyResult, RecordChangeset } from '@/features/records/editor/types';

import { projectOperatorPreCheck, type OperatorPreCheckBaseline } from './baselines';
import { compileOperatorPreCheck, valuesAfter } from './compile';
import { operatorPreCheckFailure } from './errors';
import { asDefinitions, DEFINITIONS_PATH } from './model';

export interface RebasedChanges {
  /** What to resend. */
  changes: DraftWireChange[];
  /** Changes the server moved under — the save must not go ahead. */
  drifted: RecordDriftedField[];
}

/** One save's changes against fresh baselines (after a 409): a change whose `from` still
 *  holds is resent; one the server already made is dropped; any other drifted. */
export function rebaseChanges(
  changes: readonly DraftWireChange[],
  baselines: Record<RecordKey, Record<string, unknown>>,
): RebasedChanges {
  const out: DraftWireChange[] = [];
  const drifted: RecordDriftedField[] = [];
  for (const c of changes) {
    const path = c.field ?? '';
    const rec = `${c.entity}:${c.id}`;
    const current = baselines[rec]?.[path];
    if (draftValuesEqual(c.from, current)) out.push(c);
    else if (draftValuesEqual(c.to, current)) continue;
    else drifted.push({ key: changeKey(rec, path), entity: c.entity, id: c.id, field: path, current, changed_by: null });
  }
  return { changes: out, drifted };
}

interface Sent {
  changes: readonly DraftWireChange[];
  result: OperatorPreCheck;
}

function receipt({ changes }: Sent): RecordApplyResult {
  return {
    applied: changes.map((c) => ({ rec: `${c.entity}:${c.id}`, path: c.field ?? c.action ?? '', to: c.to })),
    audit_id: null,
    audit_uuid: null,
    event_tag: null,
    saved_ts: new Date().toISOString(),
    changes: changes.map((c) => ({
      key: changeKey(`${c.entity}:${c.id}`, c.field ?? c.action ?? ''),
      entity: c.entity,
      id: c.id,
      field: c.field ?? null,
      action: c.action ?? null,
      outcome: 'change',
      from: c.from,
      to: c.to,
    })),
    events: [],
    follow_ups: [],
    warnings: [],
  };
}

export interface OperatorPrecheckApplyInput {
  topId: number;
  /** The baseline of the latest GET (a ref: read at save time). */
  baselineRef: MutableRefObject<OperatorPreCheckBaseline>;
  onSaved: (result: RecordApplyResult) => void;
}

export function useOperatorPrecheckApply({ topId, baselineRef, onSaved }: OperatorPrecheckApplyInput): RecordApplyMutation {
  const qc = useQueryClient();

  const send = async (changes: readonly DraftWireChange[], base: OperatorPreCheckBaseline, reason: string): Promise<Sent> => {
    const body = compileOperatorPreCheck(changes, base, reason);
    try {
      return { changes, result: await putOperatorPreCheck(topId, body) };
    } catch (e) {
      const refused = preCheckErrors(e);
      if (refused) {
        // Name custom fields as the save sent them, else as stored.
        const sentDefs = asDefinitions(valuesAfter(changes, base)(DEFINITIONS_PATH));
        throw operatorPreCheckFailure(refused, {
          rec: base.rec,
          sent: changes,
          definitions: [...sentDefs, ...base.data.definitions],
        });
      }
      throw e;
    }
  };

  const refetch = async (): Promise<OperatorPreCheckBaseline> =>
    projectOperatorPreCheck(
      await qc.fetchQuery({ queryKey: operatorPreCheckKey(topId), queryFn: ({ signal }) => getOperatorPreCheck(topId, signal), staleTime: 0 }),
      topId,
    );

  const mutation = useMutation({
    mutationFn: async (body: RecordChangeset): Promise<Sent> => {
      const changes = body.changes as DraftWireChange[];
      const reason = body.description ?? '';
      try {
        return await send(changes, baselineRef.current, reason);
      } catch (e) {
        if (!isPreCheckModified(e)) throw e;
      }
      // 409: what moved under this save?
      const fresh = await refetch();
      const rebased = rebaseChanges(changes, fresh.paths);
      if (rebased.drifted.length > 0) throw { drifted: rebased.drifted };
      try {
        return await send(rebased.changes, fresh, reason);
      } catch (e) {
        if (!isPreCheckModified(e)) throw e;
      }
      const again = rebaseChanges(changes, (await refetch()).paths);
      if (again.drifted.length > 0) throw { drifted: again.drifted };
      throw new Error('The pre-check-in settings changed again while saving — nothing was saved. Save again.');
    },
    onSuccess: (sent) => {
      onSaved(receipt(sent));
      qc.setQueryData(operatorPreCheckKey(topId), sent.result);
    },
  });

  return { mutate: mutation.mutate, isPending: mutation.isPending };
}
